import { Table, Button, Spinner, UncontrolledTooltip } from "reactstrap";
import { withNamespaces } from "react-i18next";
import DateUtils from "src/services/utils/DateUtils";
import { EMPTY_LIST, INTERNATIONAL_DATE_FORMAT } from 'src/common/constants';
import { useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "src/modules/3rd-party-management/constants";
import { toast } from "react-toastify";
import { connect } from "react-redux";
import { Accordion } from "react-bootstrap";
import { RiFileList3Line, RiInformationLine } from "react-icons/ri";
import { useQuery } from "@tanstack/react-query";
import KYBService from "src/modules/3rd-party-management/apis/admin/KYBService";

export const COMPANY_INFO_DOCUMENT_TYPES = {
    extract: {
        label: 'Extract',
        description: 'The regular real-time extract.',
    },
    history: {
        label: 'History',
        description: 'A history of changes to the extract since the digitization of the register.',
    },
    legacy: {
        label: 'Legacy',
        description: 'A version of the extract from before digitization.',
    },
    extract_data: {
        label: 'Extract Data',
        description: 'The extract data in structured XML format.',
    },
    shareholders: {
        label: 'Shareholders',
        description: 'A legal document containing the published shareholders.',
    },
    articles: {
        label: 'Articles',
        description: 'A legal articles of association document.',
    },
    balance_statements: {
        label: 'Balance Statements',
        description: 'A balance statements document.',
    },
    free_balance_statements: {
        label: 'Free Balance Statements',
        description: 'Free balance statements documents.',
    },
    transparency_register: {
        label: 'Transparency Register',
        description: 'Transparency register documents.',
    },
};

const Documents = ({
    t,
    token,
    activeAccordion,
    setActiveAccordion,
    detailsQuery
}) => {
    const [downloads, setDownloads] = useState([]);
    const idInSource = detailsQuery.data?.company?.idInSource;

    const {
        data: documents,
        isFetching: fetchingDocuments
    } = useQuery({
        queryKey: [
            '3rd-party-management-kyb-get-organization-details',
            'documents',
            idInSource
        ],
        queryFn: async () => {
            const service = KYBService.getInstance();

            return await service.getDocuments(idInSource, {});
        },
        cacheTime: 10,
        refetchOnWindowFocus: false,
        enabled: !!idInSource,
        retry: 0,
        onError: () => {
            toast(t('Error'), {
                type: 'error'
            });
        }
    });

    const handleDownload = async ({ id, name }) => {
        const cancelToken = axios.CancelToken.source()

        setDownloads((currentState) => {
            return [
                ...currentState,
                {
                    id: id,
                    percent: 0,
                    status: 'downloading',
                    cancelToken: cancelToken
                }
            ]
        });

        let loadedBytes = 0

        try {
            const response = await axios.get(
                `${API_BASE_URL}/kyb/document/${id}/download`,
                {
                    responseType: 'blob',
                    headers: {
                        Authorization: `Bearer ${token}`
                    },
                    onDownloadProgress: (progressEvent) => {
                        if (!progressEvent.total) return;
                        loadedBytes = progressEvent.loaded

                        const percentCompleted = Math.round(
                            (loadedBytes * 100) / progressEvent.total
                        );

                        setDownloads((currentState) => {
                            return currentState.map((downloadItem) => {
                                if(downloadItem.id === id){
                                    return {
                                        ...downloadItem,
                                        percent: percentCompleted
                                    }
                                }

                                return downloadItem;
                            });
                        });
                    },
                    cancelToken: cancelToken.token
                }
            );

            const url = window.URL.createObjectURL(
                new Blob([response.data], { type: '*' })
            )

            const link = document.createElement('a')

            link.href = url
            link.setAttribute('download', `${name}_${id}.pdf`)

            document.body.appendChild(link)

            link.click()
            link.remove()

            setDownloads((currentState) => {
                return currentState.filter((downloadItem) => {
                    return downloadItem.id !== id
                });
            });
        }
        catch (error) {
            let status;
            if (axios.isCancel(error)) {
                status = 'canceled'
                toast(t('Download cancelled.'), {
                    type: 'success',
                })
            }
            else {
                status = 'faild'
                toast(t('An error occurred while downloading the file.'), {
                    type: 'error'
                })
            }

            setDownloads((currentState) => {
                return currentState.map((downloadItem) => {
                    if (downloadItem.id === id) {
                        return {
                            ...downloadItem,
                            percent: 0,
                            status: status,
                            cancelToken: null
                        }
                    }

                    return downloadItem;
                });
            });
        }
    }

    const dateUtils = new DateUtils();

    const renderTableBody = () => {
        if (fetchingDocuments) {
            return (
                <tr>
                    <td colSpan={6} className="text-center">
                        {t('Fetching')}...
                    </td>
                </tr>
            )
        }

        if (!documents?.items?.length) {
            return (
                <tr>
                    <td colSpan={6} className="text-center">
                        {t(EMPTY_LIST)}
                    </td>
                </tr>
            )
        }

        return documents.items.map((doc, index) => {
            const downloadItem = downloads.find((download) => download.id === doc.id);
            const isDownloading = downloadItem?.status === 'downloading'

            const docTypeOption = COMPANY_INFO_DOCUMENT_TYPES[doc.type];

            return (
                <tr key={index}>
                    <td>
                        {dateUtils.convertDateToDate(doc.date, INTERNATIONAL_DATE_FORMAT)}
                    </td>
                    <td>
                        {t(docTypeOption?.label || doc.type)}

                        {docTypeOption?.description ? (
                            <>
                                <span
                                    id={`document-${doc.id}-tooltip`}
                                    className="ms-1"
                                    style={{ cursor: "pointer" }}
                                >
                                    <RiInformationLine />
                                </span>
                                <UncontrolledTooltip
                                    target={`document-${doc.id}-tooltip`}
                                >
                                    {t(docTypeOption?.description)}
                                </UncontrolledTooltip>
                            </>
                        ) : null}
                    </td>
                    <td>{doc.register}</td>
                    <td>{doc.number}</td>
                    <td>{doc.court}</td>
                    <td>
                        <Button
                            color="primary"
                            outline
                            size="sm"
                            onClick={() => handleDownload(doc)}
                            disabled={isDownloading}
                        >
                            {isDownloading ? (
                                <>
                                    <Spinner
                                        animation='border'
                                        variant='primary'
                                        size='sm'
                                        className='me-1'
                                    />
                                    {t('Downloading')}{` ${downloadItem.percent}%`}
                                </>
                            ) : (
                                <>
                                    <i className='ri-download-2-line me-1' />
                                    {t(
                                        'Download'
                                    )}
                                </>
                            )}
                        </Button>
                    </td>
                </tr>
            )
        })
    }

    return (
        <Accordion.Item
            onClick={() => setActiveAccordion('documents')}
            className={activeAccordion === 'documents' ? 'active' : ''}
            eventKey={'documents'}
        >
            <Accordion.Header>
                <RiFileList3Line className="me-2" size="24" />
                <span>
                    {t('Associated Documents')}
                </span>
            </Accordion.Header>

            <Accordion.Body>
                <div className="shareholders-section mb-5">
                    <Table striped size="sm" aria-label="Shareholders table">
                        <caption className="visually-hidden">
                            {t('List of documents related to this organization.')}
                        </caption>
                        <thead
                            style={{
                                position: 'sticky',
                                top: 0,
                                background: 'white',
                                zIndex: 2
                            }}
                        >
                            <tr>
                                <th scope="col">{t('Fetched Date')}</th>
                                <th scope="col">{t('Document Type')}</th>
                                <th scope="col">{t('Register')}</th>
                                <th scope="col">{t('Register Number')}</th>
                                <th scope="col">{t('Court')}</th>
                                <th scope="col">{t('Download')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {renderTableBody()}
                        </tbody>
                    </Table>
                </div>
            </Accordion.Body>
        </Accordion.Item>
    );
}

const mapToState = (state) => {
    const { token } = state.Login;

    return {
        token
    }
}

export default withNamespaces()(
    connect(mapToState)(
        Documents
    )
);
